import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useMqtt } from '../mqtt/useMqtt';
import { useDraftPresence } from '../mqtt/useDraftPresence';
import { useUser } from '../context/UserContext';
import { CorpsIcon } from '../components/CorpsIcon';
import { Nav } from '../components/Nav';
import type { SeasonCorps, DraftState, League, PickPreview } from '../types/api';
import {
  getPickSecondsRemaining, formatPickTimer, isPickTimerLow,
  usesDraftBudget as usesDraftBudgetFn, getEmptyCaptions, shouldWarnEmptyCaption,
} from './DraftRoom.helpers';

const CAPTION_SHORT: Record<string, string> = {
  GeneralEffectCombined: 'GE',
  GeneralEffect1:        'GE-Vis',
  GeneralEffect2:        'GE-Mus',
  VisualCombined:        'Vis',
  Visual:                'Vis',
  Colorguard:            'CG',
  VisualProficiency:     'VP',
  VisualAnalysis:        'VA',
  MusicCombined:         'Mus',
  Brass:                 'Br',
  Percussion:            'Perc',
  MusicAnalysis:         'MA',
};

const H_GAP: Partial<Record<number, number>> = { 3: 25, 4: 18, 5: 12 };

export function DraftRoom() {
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const navigate = useNavigate();

  const [league, setLeague] = useState<League | null>(null);
  const [corps, setCorps] = useState<SeasonCorps[]>([]);
  const [selectedCell, setSelectedCell] = useState<{ corpsId: string; caption: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'order' | 'picks'>('order');
  const [activePicksPlayer, setActivePicksPlayer] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [error, setError] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'board' | 'more'>('board');

  const currentDrafterRef = useRef<HTMLDivElement>(null);
  const draftState = useMqtt<DraftState>(`dcf/leagues/${id}/draft`);
  const pickPreview = useMqtt<PickPreview>(`dcf/leagues/${id}/draft/pick`);
  const { publishPickPreview } = useDraftPresence(id!, user?.id);

  useEffect(() => {
    if (!id) return;
    api.getLeague(id)
      .then(l => {
        setLeague(l);
        if (l.seasonId) {
          api.getSeasonCorps(l.seasonId).then(setCorps).catch(() => {});
        }
      })
      .catch(() => setError('Failed to load league.'));
  }, [id]);

  // Redirect guard — only allow Open, InProgress, Completed
  useEffect(() => {
    if (!league) return;

    if (league.draftStatus === 'NotStarted') {
      navigate(`/leagues/${id}`);
    }
  }, [league, id, navigate]);

  // Countdown timer — ticks during Open lobby, Scheduled lobby, or while a pick timer is running
  useEffect(() => {
    const needsTicking = draftState?.status === 'Open' || league?.draftStatus === 'Scheduled' ||
      (draftState?.status === 'InProgress' && !!draftState?.pickDeadline);
    if (!needsTicking) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [draftState?.status, league?.draftStatus, draftState?.pickDeadline]);

  // Scroll current drafter into view in the Draft Order panel on each pick
  useEffect(() => {
    if (activeTab === 'order') {
      currentDrafterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [draftState?.currentPickNumber]);

  if (error) return <div style={{ padding: 16, color: 'var(--text)' }}>{error}</div>;
  if (!league || !draftState) return <div style={{ padding: 16, color: 'var(--text-muted)' }}>Loading…</div>;

  const status = draftState.status;
  const mainTotalPicks = draftState.mainTotalPicks ?? 0;
  const inMakeupPhase = status === 'InProgress' && mainTotalPicks > 0 && draftState.currentPickNumber >= mainTotalPicks;
  const isMyTurn = status === 'InProgress' && (
    inMakeupPhase
      ? (draftState.makeupQueue ?? []).includes(user?.id ?? '')
      : draftState.currentDrafterId === user?.id
  );
  const takenSet = new Set(draftState.picks.map(p => `${p.corpsId}|${p.caption}`));
  const isTaken = (corpsId: string, caption: string) => takenSet.has(`${corpsId}|${caption}`);
  const isOnline = (userId: string) => (draftState.onlineUserIds ?? []).includes(userId);

  const currentDrafter = draftState.members.find(m => m.userId === draftState.currentDrafterId);

  const usesDraftBudget = usesDraftBudgetFn(draftState, league);
  const draftBudget = draftState.draftBudget ?? league.draftBudget ?? 0;
  const corpsPerCaption = draftState.corpsPerCaption ?? league.corpsPerCaption ?? 0;

  // In budget-mode drafts, warn a member when they currently have a caption with
  // no corps drafted, since empty captions are allowed but easy to overlook while
  // allocating a budget.
  const myPickCount = draftState.picks.filter(p => p.userId === user?.id).length;
  const emptyCaptions = usesDraftBudget
    ? getEmptyCaptions(league.draftableCaptions, draftState.picks, user?.id)
    : [];
  const showEmptyCaptionWarning = shouldWarnEmptyCaption({
    usesDraftBudget,
    status,
    myPickCount,
    emptyCaptionCount: emptyCaptions.length,
  });

  // Pick preview is valid only when the cell is still available and it's the current drafter's preview
  const validPreview = (
    pickPreview &&
    pickPreview.userId === draftState.currentDrafterId &&
    !isMyTurn &&
    !isTaken(pickPreview.corpsId, pickPreview.caption)
  ) ? pickPreview : null;

  const handleCellClick = (corpsId: string, caption: string) => {
    if (!isMyTurn || isTaken(corpsId, caption)) return;
    setSelectedCell({ corpsId, caption });
    publishPickPreview(corpsId, caption);

    // Stage the selection server-side too (not just this broadcast preview) so the pick
    // timer can submit it automatically if it expires before Submit Pick is clicked.
    // Makeup-phase picks have no timer and the backend rejects selection during it.
    if (id && !inMakeupPhase) {
      api.selectPick(id, corpsId, caption).catch(() => {});
    }
  };

  const submitPick = async () => {
    if (!id || !selectedCell || submitting) return;
    setSubmitting(true);
    try {
      await api.submitPick(id, selectedCell.corpsId, selectedCell.caption);
      setSelectedCell(null);
    }
    catch {
      // Most often lost the race with the pick timer expiring and auto-submitting this
      // same selection - draftState updating out from under isMyTurn already clears the
      // stale Submit UI, so there's nothing further to reconcile here.
    }
    finally {
      setSubmitting(false);
    }
  };

  const skipPick = () =>
  {
    if (id) api.skipPick(id).catch(() => {});
  };

  const startDraft = () =>
  {
    if (id) api.startDraft(id).catch(() => {});
  };

  const getCountdown = () => {
    if (!league.draftStartTime) return '--:--:--';
    const diff = new Date(league.draftStartTime).getTime() - now;
    if (diff <= 0) return '00:00:00';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
  };

  // ── Bar ───────────────────────────────────────────────────────────────────

  const renderBar = () => {
    const hasScheduledTime = !!league.draftStartTime;
    const commissionerName = draftState.members.find(m => m.userId === league.commissionerUserId)?.displayName ?? 'the commissioner';
    const memberCount = draftState.draftOrder.length || 1;
    const round = Math.floor(draftState.currentPickNumber / memberCount) + 1;
    const pick = (draftState.currentPickNumber % memberCount) + 1;
    const selectedCorps = corps.find(c => c.id === selectedCell?.corpsId);
    const canSubmit = isMyTurn && !!selectedCell && !submitting;
    const selectionLabel = (selectedCorps && selectedCell)
      ? `${selectedCorps.name} · ${CAPTION_SHORT[selectedCell.caption] ?? selectedCell.caption}`
      : '— · —';

    let barBg: string;
    let barAccent: string;

    if (status === 'Open') {
      barBg = 'linear-gradient(90deg, #0f1a0f, #101810)';
      barAccent = 'var(--green)';
    }
    else if (status === 'InProgress') {
      barBg = isMyTurn
        ? 'linear-gradient(90deg, #2e1065, #1a1535)'
        : 'linear-gradient(90deg, #651010, #351515)';
      barAccent = isMyTurn ? 'var(--accent)' : 'var(--red)';
    }
    else {
      barBg = 'var(--surface)';
      barAccent = 'var(--border)';
    }

    const renderStatus = () => {
      if (status === 'Open') {
        if (hasScheduledTime) {
          return (
            <>
              <div style={{ flexShrink: 0 }}>
                <div style={{ fontSize: 9, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--green)', fontWeight: 700 }}>Draft Begins In</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text-heading)', fontVariantNumeric: 'tabular-nums' }}>{getCountdown()}</div>
                {league.draftStartTime && (
                  <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{new Date(league.draftStartTime).toLocaleString()}</div>
                )}
              </div>
              {league.isCommissioner && (
                <button
                  onClick={startDraft}
                  style={{ border: '1px solid var(--green-border)', color: 'var(--green)', background: 'transparent', borderRadius: 5, padding: '5px 12px', fontSize: 10, cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}
                >
                  Start Early
                </button>
              )}
            </>
          );
        }

        if (league.isCommissioner) {
          return (
            <button
              onClick={startDraft}
              style={{ border: '1px solid var(--green-border)', color: 'var(--green)', background: 'transparent', borderRadius: 5, padding: '5px 12px', fontSize: 10, cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}
            >
              Start Draft
            </button>
          );
        }

        return (
          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Waiting for {commissionerName} to start the draft
          </span>
        );
      }

      if (status === 'InProgress') {
        if (inMakeupPhase) {
          const makeupCounts: Record<string, number> = {};
          (draftState.makeupQueue ?? []).forEach(userId => {
            makeupCounts[userId] = (makeupCounts[userId] ?? 0) + 1;
          });
          const pendingPlayers = Object.entries(makeupCounts).map(([userId, count]) => {
            const member = draftState.members.find(m => m.userId === userId);
            return { userId, displayName: member?.displayName ?? 'Unknown', count };
          });

          return (
            <>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, letterSpacing: '0.5px', textTransform: 'uppercase', color: barAccent, fontWeight: 700 }}>
                  Makeup Picks
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {pendingPlayers.map(p =>
                    p.count > 1 ? `${p.displayName} ×${p.count}` : p.displayName
                  ).join(', ')} still to pick
                </div>
              </div>
              {isMyTurn && (
                <div className="draft-bar-submit-row">
                  <div style={{ flexShrink: 0 }}>
                    <div style={{ fontSize: 7, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-faint)' }}>Selected</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{selectionLabel}</div>
                  </div>
                  <button
                    onClick={submitPick}
                    disabled={!canSubmit}
                    style={{
                      background: canSubmit ? 'var(--accent)' : 'var(--border)',
                      color: canSubmit ? '#0d0f14' : 'var(--text-faint)',
                      border: 'none', borderRadius: 5, padding: '5px 14px',
                      fontSize: 10, fontWeight: 800, letterSpacing: '0.5px',
                      textTransform: 'uppercase', cursor: canSubmit ? 'pointer' : 'not-allowed',
                      flexShrink: 0,
                    }}
                  >
                    Submit Pick
                  </button>
                </div>
              )}
            </>
          );
        }

        const pickSecondsRemaining = getPickSecondsRemaining(draftState.pickDeadline, now);
        const pickTimerLow = isPickTimerLow(pickSecondsRemaining);

        return (
          <>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, letterSpacing: '0.5px', textTransform: 'uppercase', color: barAccent, fontWeight: 700 }}>
                {isMyTurn ? 'On the Clock' : 'Now Picking'}
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-heading)' }}>
                {isMyTurn ? (user?.displayName ?? '—') : (currentDrafter?.displayName ?? '—')}
                <span style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>· Round {round} · Pick {pick}</span>
              </div>
            </div>
            {pickSecondsRemaining !== null && (
              <div style={{ flexShrink: 0, textAlign: 'center' }}>
                <div style={{ fontSize: 7, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-faint)' }}>Pick Timer</div>
                <div
                  data-testid="pick-timer"
                  style={{
                    fontSize: 16, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
                    color: pickTimerLow ? 'var(--red)' : 'var(--text-heading)',
                  }}
                >
                  {formatPickTimer(pickSecondsRemaining)}
                </div>
              </div>
            )}
            {isMyTurn && (
              <div className="draft-bar-submit-row">
                <div style={{ flexShrink: 0 }}>
                  <div style={{ fontSize: 7, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-faint)' }}>Selected</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{selectionLabel}</div>
                </div>
                <button
                  onClick={submitPick}
                  disabled={!canSubmit}
                  style={{
                    background: canSubmit ? 'var(--accent)' : 'var(--border)',
                    color: canSubmit ? '#0d0f14' : 'var(--text-faint)',
                    border: 'none', borderRadius: 5, padding: '5px 14px',
                    fontSize: 10, fontWeight: 800, letterSpacing: '0.5px',
                    textTransform: 'uppercase', cursor: canSubmit ? 'pointer' : 'not-allowed',
                    flexShrink: 0,
                  }}
                >
                  Submit Pick
                </button>
              </div>
            )}
            {!isMyTurn && !inMakeupPhase && league.isCommissioner && (
              <button
                onClick={skipPick}
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 5, padding: '5px 10px', fontSize: 10, cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}
              >
                Skip Pick
              </button>
            )}
          </>
        );
      }

      return (
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>Draft Complete</div>
      );
    };

    return (
      <div className="draft-bar" style={{
        background: barBg,
        borderBottom: `2px solid ${barAccent}`,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
      }}>
        <Link
          to={`/leagues/${id}`}
          style={{
            fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
            background: 'rgba(255,255,255,0.07)', border: '1px solid var(--border)',
            borderRadius: 4, padding: '3px 9px', textDecoration: 'none', flexShrink: 0,
          }}
        >
          ← League
        </Link>
        <div className="draft-bar-separator" style={{ width: 1, height: 32, background: 'var(--border)', flexShrink: 0 }} />
        <div className="draft-bar-league-name" style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-heading)', flexShrink: 0 }}>
          {league.name}
        </div>
        <div className="draft-bar-separator" style={{ width: 1, height: 32, background: 'var(--border)', flexShrink: 0 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
          {renderStatus()}
        </div>
      </div>
    );
  };

  // ── Pick grid ─────────────────────────────────────────────────────────────

  const renderGrid = () => {
    const captions = league.draftableCaptions!;
    const gridLocked = status !== 'InProgress' || !isMyTurn;
    const cellWidth = captions.length <= 3 ? Math.min(88, Math.floor(176 / captions.length)) : 60;
    const hGap = H_GAP[captions.length] ?? 6;

    const myPicksByCaption: Record<string, number> = {};
    draftState.picks
      .filter(p => p.userId === user?.id)
      .forEach(p => { myPicksByCaption[p.caption] = (myPicksByCaption[p.caption] ?? 0) + 1; });
    const budgetSpent = usesDraftBudget && myPickCount >= draftBudget;
    const isCaptionFull = (cap: string) =>
      usesDraftBudget
        ? budgetSpent
        : (myPicksByCaption[cap] ?? 0) >= corpsPerCaption;

    return (
      <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {status === 'Open' && (
          <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-heading)', marginBottom: 8, alignSelf: 'anchor-center' }}>
            Pick board locked until the draft begins
          </div>
        )}
        {showEmptyCaptionWarning && (
          <div
            role="alert"
            style={{
              fontSize: 10, color: 'var(--red)', background: 'var(--red-bg, rgba(224,108,117,0.12))',
              border: '1px solid var(--red)', borderRadius: 5, padding: '6px 10px', marginBottom: 8,
              maxWidth: 480, textAlign: 'center',
            }}
          >
            You have no corps drafted for {emptyCaptions.map(cap => CAPTION_SHORT[cap] ?? cap).join(', ')}.
          </div>
        )}
        <table style={{ borderCollapse: 'separate', borderSpacing: `${hGap}px 4px` }}>
          <thead>
            <tr>
              {captions.map(cap => (
                <th key={cap} style={{
                  width: cellWidth,
                  fontSize: 14,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: 'var(--text-heading)',
                  paddingTop: 4,
                  paddingBottom: 6,
                  textAlign: 'center',
                  fontWeight: 600,
                  position: 'sticky',
                  top: 0,
                  background: 'var(--bg)',
                  zIndex: 1,
                }}>
                  {CAPTION_SHORT[cap] ?? cap}
                  <div style={{ fontSize: 12, color: (!usesDraftBudget && isCaptionFull(cap)) || (usesDraftBudget && (myPicksByCaption[cap] ?? 0) === 0) ? 'var(--red)' : 'var(--text-heading)', marginTop: 1, fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>
                    {usesDraftBudget ? (myPicksByCaption[cap] ?? 0) : `${myPicksByCaption[cap] ?? 0}/${corpsPerCaption}`}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {corps.filter(c => !captions.every(cap => isTaken(c.id, cap))).map(c => (
              <tr key={c.id}>
                {captions.map(cap => {
                  const taken = isTaken(c.id, cap);
                  const selected = !gridLocked && selectedCell?.corpsId === c.id && selectedCell?.caption === cap;
                  const previewed = !taken && !selected && validPreview?.corpsId === c.id && validPreview?.caption === cap;
                  const isLobby = status === 'Open';
                  const captionFull = isCaptionFull(cap) && !taken;
                  const iconSize = 36;

                  let bg = '#1c1f2c';
                  let border = '1px solid var(--border)';
                  let boxShadow = 'none';
                  const cursor = gridLocked || taken || captionFull ? 'not-allowed' : 'pointer';
                  let content: ReactNode;

                  if (taken) {
                    bg = '#12141a';
                    border = '1px solid var(--border-subtle)';
                    content = <CorpsIcon name={c.name} iconUrl={c.iconUrl} size={iconSize} style={{ opacity: 0.25 }} />;
                  }
                  else if (selected) {
                    bg = 'var(--accent-bg)';
                    border = '2px solid var(--accent)';
                    boxShadow = '0 0 10px var(--accent-bg)';
                    content = <CorpsIcon name={c.name} iconUrl={c.iconUrl} size={iconSize - 2} style={{ outline: '1px solid var(--accent)', outlineOffset: 2 }} />;
                  }
                  else if (captionFull) {
                    bg = '#12141a';
                    border = '1px solid var(--border-subtle)';
                    content = <CorpsIcon name={c.name} iconUrl={c.iconUrl} size={iconSize} style={{ opacity: 0.25 }} />;
                  }
                  else if (previewed) {
                    const drafter = draftState.members.find(m => m.userId === validPreview!.userId);
                    bg = '#1e1430';
                    border = '1px dashed var(--accent-border)';
                    content = (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CorpsIcon name={c.name} iconUrl={c.iconUrl} size={iconSize - 10} />
                        <span style={{ color: 'var(--text-muted)', fontSize: 7, lineHeight: 1 }}>
                          {drafter?.displayName.split(' ')[0] ?? ''}
                        </span>
                      </div>
                    );
                  }
                  else {
                    content = <CorpsIcon name={c.name} iconUrl={c.iconUrl} size={iconSize} />;
                  }

                  return (
                    <td key={cap}>
                      <div
                        onClick={() => handleCellClick(c.id, cap)}
                        style={{
                          width: cellWidth,
                          height: 56,
                          background: bg,
                          border,
                          borderRadius: 4,
                          boxShadow,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor,
                          opacity: isLobby ? 0.45 : 1,
                          userSelect: 'none',
                          transition: 'background 0.1s',
                          pointerEvents: (gridLocked || captionFull) ? 'none' : 'auto',
                        }}
                      >
                        {content}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Side panel — Draft Order tab ──────────────────────────────────────────

  const renderDraftOrderTab = () => {
    if (status === 'Open') {
      const onlineCount = draftState.draftOrder.filter(m => isOnline(m.userId)).length;

      return (
        <div>
          <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-faint)', marginBottom: 8 }}>Draft Order</div>
          {draftState.draftOrder.map((m, i) => (
            <div key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--text-muted)', flexShrink: 0 }}>
                {i + 1}
              </div>
              <span style={{ fontSize: 7, color: isOnline(m.userId) ? 'var(--green)' : 'var(--text-faint)', flexShrink: 0 }}>
                {isOnline(m.userId) ? '●' : '○'}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-heading)' }}>{m.displayName}</span>
            </div>
          ))}
          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 8 }}>
            {onlineCount} of {draftState.draftOrder.length} members online
          </div>
        </div>
      );
    }

    const n = draftState.draftOrder.length;
    const totalPicks = mainTotalPicks;

    const upcomingOrder: Array<{ userId: string; displayName: string }> = [];

    if (status === 'InProgress') {
      for (let pick = draftState.currentPickNumber + 1; pick < Math.min(draftState.currentPickNumber + 6, totalPicks); pick++) {
        const round = Math.floor(pick / n);
        const pos = pick % n;
        const idx = round % 2 === 0 ? pos : n - 1 - pos;
        upcomingOrder.push(draftState.draftOrder[idx]);
      }
    }

    return (
      <div>
        {draftState.picks.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-faint)', marginBottom: 6 }}>Completed</div>
            {draftState.picks.map(p => (
              <div key={p.pickNumber} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', opacity: 0.55 }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: 'var(--text-muted)', flexShrink: 0 }}>
                  {p.pickNumber + 1}
                </div>
                <span style={{ fontSize: 10, color: 'var(--text)' }}>{p.displayName} — {p.corpsName} ({p.caption})</span>
              </div>
            ))}
          </div>
        )}
        {status === 'InProgress' && currentDrafter && (
          <div ref={currentDrafterRef} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', margin: '4px 0', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 5 }}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--accent-bg)', border: '1px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: 'var(--accent)', flexShrink: 0 }}>
              {draftState.currentPickNumber + 1}
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-heading)' }}>{currentDrafter.displayName}</span>
          </div>
        )}
        {upcomingOrder.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-faint)', marginBottom: 6 }}>Up Next</div>
            {upcomingOrder.map((m, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', opacity: 0.6 }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: 'var(--text-muted)', flexShrink: 0 }}>
                  {draftState.currentPickNumber + i + 2}
                </div>
                <span style={{ fontSize: 10, color: 'var(--text)' }}>{m.displayName}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Side panel — Picks tab ────────────────────────────────────────────────

  const renderPicksTab = () => {
    const players = draftState.members;
    const effectivePicksPlayer = activePicksPlayer ?? players[0]?.userId ?? null;
    const currentPlayer = players.find(m => m.userId === effectivePicksPlayer) ?? players[0];
    if (!currentPlayer) return null;

    const captions = league.draftableCaptions!;
    const playerPicks = draftState.picks.filter(p => p.userId === currentPlayer.userId);

    return (
      <div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
          {players.map(m => (
            <button
              key={m.userId}
              onClick={() => setActivePicksPlayer(m.userId)}
              style={{
                padding: '4px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600,
                cursor: 'pointer', border: 'none',
                background: effectivePicksPlayer === m.userId ? 'var(--accent)' : 'var(--surface)',
                color: effectivePicksPlayer === m.userId ? '#0d0f14' : 'var(--text-muted)',
              }}
            >
              {m.displayName.split(' ')[0]}
            </button>
          ))}
        </div>
        {captions.map(cap => {
          const capPicks = playerPicks.filter(p => p.caption === cap);
          const filled = capPicks.length;
          const total = usesDraftBudget ? filled : corpsPerCaption;
          // In budget mode an empty caption is allowed, so highlight it as a
          // gentle warning rather than a missing required slot.
          const emptyInBudget = usesDraftBudget && filled === 0;

          return (
            <div key={cap} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-faint)' }}>{cap}</div>
                <div style={{
                  fontSize: 8, padding: '1px 6px', borderRadius: 8, fontWeight: 700,
                  background: filled > 0 ? 'var(--accent-bg)' : 'var(--surface)',
                  color: emptyInBudget ? 'var(--red)' : (filled > 0 ? 'var(--accent)' : 'var(--text-faint)'),
                  border: `1px solid ${emptyInBudget ? 'var(--red)' : (filled > 0 ? 'var(--accent-border)' : 'var(--border)')}`,
                }}>
                  {usesDraftBudget ? filled : `${filled} / ${total}`}
                </div>
              </div>
              {emptyInBudget && (
                <div style={{ padding: '5px 8px', border: '1px dashed var(--red)', borderRadius: 5, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontStyle: 'italic', color: 'var(--red)' }}>No corps drafted</span>
                </div>
              )}
              {Array.from({ length: total }).map((_, i) => {
                const pick = capPicks[i];

                if (pick) {
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, marginBottom: 4 }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: 'var(--accent)', flexShrink: 0 }}>
                        #{pick.pickNumber + 1}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-heading)' }}>{pick.corpsName}</div>
                        <div style={{ fontSize: 8, color: 'var(--text-muted)' }}>Pick #{pick.pickNumber + 1} overall</div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={i} style={{ padding: '5px 8px', border: '1px dashed var(--border)', borderRadius: 5, marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontStyle: 'italic', color: 'var(--text-faint)' }}>Empty</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  // ── Layout ────────────────────────────────────────────────────────────────

  return (
    <>
      <Nav />
      <div style={{ height: 'calc(100vh - 44px)', overflow: 'hidden', background: 'var(--bg)', color: 'var(--text)', display: 'flex', flexDirection: 'column' }}>
        {renderBar()}
        <div className="draft-mobile-toggle">
          <button
            className={mobileView === 'board' ? 'active' : ''}
            onClick={() => setMobileView('board')}
          >
            Draft Board
          </button>
          <button
            className={mobileView === 'more' ? 'active' : ''}
            onClick={() => setMobileView('more')}
          >
            More
          </button>
        </div>
        <div data-mobile-view={mobileView} style={{ flex: 1, maxWidth: 1200, width: '100%', margin: '0 auto', padding: '0 20px', boxSizing: 'border-box', display: 'flex', overflow: 'hidden' }}>
          {/* Left — grid panel */}
          <div className="draft-grid-panel" style={{ flex: 1, flexDirection: 'column', overflow: 'hidden' }}>
            {renderGrid()}
          </div>
          {/* Right — side panel */}
          <div className="draft-side-panel" style={{ background: 'var(--surface-2)', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0 }}>
              {(['order', 'picks'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1, padding: '10px 0', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: 'transparent', border: 'none',
                    color: activeTab === tab ? 'var(--accent)' : 'var(--text-muted)',
                    borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
                  }}
                >
                  {tab === 'order' ? 'Draft Order' : 'Picks'}
                </button>
              ))}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              {activeTab === 'order' ? renderDraftOrderTab() : renderPicksTab()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
